<script setup lang="ts">
import { StarsProps } from "./Stars.props"

const props = withDefaults(defineProps<StarsProps>(), {
  size: 4,
  gap: 1,
  isClickable: false
})

const normalizedRating = computed(() => {
  if (!props.stars) return 0
  return Math.max(0, Math.min(5, props.stars))
})

const fullStars = computed(() => {
  const decimal = normalizedRating.value % 1
  const base = Math.floor(normalizedRating.value)
  return decimal >= 0.9 ? base + 1 : base
})

const hasPartialStar = computed(() => {
  const decimal = normalizedRating.value % 1
  return decimal >= 0.3 && decimal < 0.9 && normalizedRating.value < 5
})

const partialStarPercentage = computed(() => {
  if (!hasPartialStar.value) return 0
  const decimal = normalizedRating.value % 1
  if (decimal >= 0.3 && decimal < 0.9) return 50
  return 0
})

const emptyStars = computed(() => {
  const total = fullStars.value + (hasPartialStar.value ? 1 : 0)
  return Math.max(0, 5 - total)
})

const starOnHover = ref(0)
const selectedStar = ref(0)

const stars = (quantity: number) => (starOnHover.value = quantity)

const starQuantity = computed(() =>
  selectedStar.value > 0 ? selectedStar.value : starOnHover.value
)

const emit = defineEmits<{ (e: "onReviewSaved", review: number): void }>()

const handleReviewRegistration = (review: number) => {
  selectedStar.value = review
  emit("onReviewSaved", review)
}
</script>

<template>
  <div v-if="isClickable" class="flex cursor-pointer" :class="`gap-${gap}`">
    <UtilsIcon
      name="StarFilled.svg"
      color="yellow-main"
      v-for="x in starQuantity"
      :key="`clickable-full-${x}`"
      @click="handleReviewRegistration(x)"
      @mouseleave="starOnHover = 0"
      @mouseover="stars(x)"
      :class="`h-${size} w-${size}`"
      :preload="preloadIcons ? true : undefined"
      :fetch-priority="preloadIcons ? 'high' : undefined"
    />

    <UtilsIcon
      name="Star.svg"
      color="yellow-main"
      v-for="x in 5 - starQuantity"
      :key="`clickable-empty-${x}`"
      @mouseover="stars(x)"
      @click="handleReviewRegistration(x + selectedStar)"
      :class="`h-${size} w-${size}`"
      :preload="preloadIcons ? true : undefined"
      :fetch-priority="preloadIcons ? 'high' : undefined"
    />
  </div>

  <div
    v-else-if="normalizedRating >= 0 && normalizedRating <= 5"
    class="flex"
    :class="`gap-${gap}`"
  >
    <UtilsIcon
      name="StarFilled.svg"
      color="yellow-main"
      v-for="i in fullStars"
      :key="`full-${i}`"
      :class="`h-${size} w-${size}`"
      :preload="preloadIcons ? true : undefined"
      :fetch-priority="preloadIcons ? 'high' : undefined"
    />
    <div
      v-if="hasPartialStar"
      class="relative inline-block"
      :class="`h-${size} w-${size}`"
    >
      <UtilsIcon
        name="Star.svg"
        color="yellow-main"
        :class="`h-${size} w-${size}`"
        :preload="preloadIcons ? true : undefined"
        :fetch-priority="preloadIcons ? 'high' : undefined"
      />
      <UtilsIcon
        name="StarFilled.svg"
        color="yellow-main"
        :class="`h-${size} w-${size} star-filled-overlay absolute left-[2px] top-[0px]`"
        :style="{
          maskImage: `linear-gradient(to right, black 0%, black ${partialStarPercentage}%, transparent ${partialStarPercentage}%, transparent 100%)`,
          WebkitMaskImage: `linear-gradient(to right, black 0%, black ${partialStarPercentage}%, transparent ${partialStarPercentage}%, transparent 100%)`,
          maskSize: '100% 100%',
          WebkitMaskSize: '100% 100%',
          transform: 'scale(0.80)',
          transformOrigin: 'left center'
        }"
        :preload="preloadIcons ? true : undefined"
        :fetch-priority="preloadIcons ? 'high' : undefined"
      />
    </div>
    <UtilsIcon
      name="Star.svg"
      color="yellow-main"
      v-for="i in emptyStars"
      :key="`empty-${i}`"
      :class="`h-${size} w-${size}`"
      :preload="preloadIcons ? true : undefined"
      :fetch-priority="preloadIcons ? 'high' : undefined"
    />
  </div>
</template>

<style scoped>
.star-filled-overlay :deep(img),
.star-filled-overlay :deep(svg),
.star-filled-overlay :deep(svg path),
.star-filled-overlay :deep(svg *) {
  stroke: none !important;
  stroke-width: 0 !important;
  outline: none !important;
  border: none !important;
}
</style>
